import { useCallback, useRef, useEffect } from 'react'
import { URL_SYNC_DEBOUNCE_MS } from '@/constants/timing'
import type { Filter, Sort } from '@/types/api'

/**
 * Search params schema for datatable URL state
 */
export interface DataTableSearchParams {
  /** Serialized filters */
  filters?: string
  /** Serialized sorts */
  sorts?: string
  /** Search term */
  q?: string
}

/**
 * Parse search params into typed values
 */
export function parseSearchParams(search: DataTableSearchParams): {
  filters: Filter[]
  sorts: Sort[]
  searchTerm: string
} {
  let filters: Filter[] = []
  let sorts: Sort[] = []

  if (search.filters) {
    try {
      const parsed = JSON.parse(decodeURIComponent(search.filters))
      if (Array.isArray(parsed)) {
        filters = parsed
      }
    } catch {
      // Invalid JSON, ignore
    }
  }

  if (search.sorts) {
    try {
      const parsed = JSON.parse(decodeURIComponent(search.sorts))
      if (Array.isArray(parsed)) {
        sorts = parsed
      }
    } catch {
      // Invalid JSON, ignore
    }
  }

  return {
    filters,
    sorts,
    searchTerm: search.q ?? '',
  }
}

/**
 * Serialize state to search params
 */
export function serializeToSearchParams(state: {
  filters: Filter[]
  sorts: Sort[]
  searchTerm: string
}): DataTableSearchParams {
  const params: DataTableSearchParams = {}

  if (state.filters.length > 0) {
    params.filters = encodeURIComponent(JSON.stringify(state.filters))
  }

  if (state.sorts.length > 0) {
    params.sorts = encodeURIComponent(JSON.stringify(state.sorts))
  }

  if (state.searchTerm) {
    params.q = state.searchTerm
  }

  return params
}

/**
 * Get current search params from URL
 */
function getCurrentSearchParams(): DataTableSearchParams {
  const params = new URLSearchParams(window.location.search)
  return {
    filters: params.get('filters') ?? undefined,
    sorts: params.get('sorts') ?? undefined,
    q: params.get('q') ?? undefined,
  }
}

interface UseUrlSyncOptions {
  /** Current filters state */
  filters: Filter[]
  /** Current sorts state */
  sorts: Sort[]
  /** Current search term */
  searchTerm: string
  /** Callback when URL state changes */
  onUrlStateChange?: (state: {
    filters: Filter[]
    sorts: Sort[]
    searchTerm: string
  }) => void
  /** Debounce delay in ms (default: URL_SYNC_DEBOUNCE_MS) */
  debounceMs?: number
  /** Whether URL sync is enabled (default: true) */
  enabled?: boolean
}

/**
 * Hook for bidirectional URL sync of filters, sorts, and search term.
 *
 * Features:
 * - Debounced URL updates to avoid excessive history entries
 * - Loop prevention to avoid infinite sync cycles
 * - Hydration from URL on mount
 * - Uses browser History API directly for flexibility
 */
export function useUrlSync({
  filters,
  sorts,
  searchTerm,
  onUrlStateChange,
  debounceMs = URL_SYNC_DEBOUNCE_MS,
  enabled = true,
}: UseUrlSyncOptions) {
  // Track if we're in the middle of a navigation to prevent loops
  const isNavigatingRef = useRef(false)
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Track last synced state to avoid unnecessary updates
  const lastSyncedRef = useRef<string>('')

  // Store onUrlStateChange in a ref to avoid re-running effect
  const onUrlStateChangeRef = useRef(onUrlStateChange)
  useEffect(() => {
    onUrlStateChangeRef.current = onUrlStateChange
  }, [onUrlStateChange])

  // Track if initial hydration has been done
  const hasHydratedRef = useRef(false)

  // Hydrate state from URL on mount
  useEffect(() => {
    if (!enabled || hasHydratedRef.current) return

    const callback = onUrlStateChangeRef.current
    if (!callback) return

    hasHydratedRef.current = true

    const search = getCurrentSearchParams()
    const urlState = parseSearchParams(search)
    const hasUrlState =
      urlState.filters.length > 0 ||
      urlState.sorts.length > 0 ||
      urlState.searchTerm !== ''

    if (hasUrlState) {
      isNavigatingRef.current = true
      callback(urlState)
      // Reset the flag after a tick
      setTimeout(() => {
        isNavigatingRef.current = false
      }, 0)
    }
  }, [enabled]) // Only run on mount or when enabled changes

  // Sync state to URL (debounced)
  const syncToUrl = useCallback(() => {
    if (!enabled || isNavigatingRef.current) return

    const newParams = serializeToSearchParams({ filters, sorts, searchTerm })
    const stateKey = JSON.stringify(newParams)

    // Skip if state hasn't changed
    if (stateKey === lastSyncedRef.current) return
    lastSyncedRef.current = stateKey

    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      isNavigatingRef.current = true

      // Build new URL with updated search params
      const url = new URL(window.location.href)
      const params = url.searchParams

      // Update or remove params
      if (newParams.filters) {
        params.set('filters', newParams.filters)
      } else {
        params.delete('filters')
      }

      if (newParams.sorts) {
        params.set('sorts', newParams.sorts)
      } else {
        params.delete('sorts')
      }

      if (newParams.q) {
        params.set('q', newParams.q)
      } else {
        params.delete('q')
      }

      // Update URL without triggering navigation
      window.history.replaceState(null, '', url.toString())

      // Reset navigation flag after a tick
      setTimeout(() => {
        isNavigatingRef.current = false
      }, 0)
    }, debounceMs)
  }, [enabled, filters, sorts, searchTerm, debounceMs])

  // Trigger URL sync when state changes
  useEffect(() => {
    syncToUrl()
  }, [syncToUrl])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  // Return current URL state for reference
  return {
    urlState: parseSearchParams(getCurrentSearchParams()),
  }
}

/**
 * Validate search params for route definition
 */
export function validateDataTableSearch(
  search: Record<string, unknown>
): DataTableSearchParams {
  return {
    filters: typeof search.filters === 'string' ? search.filters : undefined,
    sorts: typeof search.sorts === 'string' ? search.sorts : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
  }
}
